import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import RichTextEditorToolbar from './RichTextEditorToolbar.jsx';
import RichTextEditorContent from './RichTextEditorContent.jsx';
import RichTextEditorAiBar from './RichTextEditorAiBar.jsx';
import {
  detectInputFormat,
  getContentPayload,
  getSelectionContext,
  isExternalValueDifferent,
  normalizeEditorInput,
  textToHtml,
} from './richTextEditor.utils.js';

const DEFAULT_PLACEHOLDER = 'Start writing...';

/** Stable fallback when `extensions` is omitted (avoid `extensions = []` per render). */
const DEFAULT_EXTENSIONS = [];

/** Stable class string for ProseMirror (avoid new options object each render). */
const EDITOR_PROSEMIRROR_CLASS = [
  'ProseMirror max-w-none min-h-[220px] px-4 py-3',
  'text-content [caret-color:rgb(var(--content)/1)]',
  'selection:bg-brand-500/25 dark:selection:bg-brand-400/25',
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
  'dark:focus-visible:ring-accent/35 dark:focus-visible:ring-offset-gray-900',
].join(' ');

/**
 * @typedef {object} RichTextEditorAiAction
 * @property {string} id
 * @property {string} label
 * @property {import('react').ComponentType<{ size?: number }>} [icon]
 * @property {boolean} [disabled]
 * @property {(payload: RichTextEditorAiPayload) => void} [onClick] — If set, runs instead of `onAiAction` for this action.
 */

/**
 * @typedef {object} RichTextEditorAiPayload
 * @property {RichTextEditorAiAction} action
 * @property {object|null} context — Editor selection / context (e.g. selected text).
 * @property {object|null} [editorApi] — Imperative API to insert or replace content.
 */

/**
 * Rich text editor (TipTap).
 *
 * **AI (toolbar icon opens a panel):**
 * - `onAiPromptApply` + prompt row: **Apply** submits the free-text prompt field.
 * - `aiActions` + `onAiAction`: quick-action chips; each calls `onAiAction(actionId, payload)` unless the item defines its own `onClick`.
 *
 * Input format behavior:
 * - If `inputFormat="markdown"`, markdown is parsed to HTML before loading TipTap.
 * - If `inputFormat="html"`, content is loaded as HTML directly.
 * - If `inputFormat="text"`, plain text is converted to paragraph HTML.
 * - If `inputFormat="auto"` (default), format is detected from the incoming value.
 *
 * **Tables:** GFM tables from markdown/HTML are supported via TipTap Table extensions
 * (`resizable: false` by default).
 *
 * **Download (optional):** when `downloadMenuEnabled` is true, the toolbar shows a Download
 * control with PDF and DOCX. Default trigger label is **Download** (`downloadTriggerLabel`);
 * use an empty string for an **icon-only** trigger (`downloadTriggerAriaLabel` for aria/title).
 * Export runs in-package via dynamic `import()` of `jspdf` and `docx`.
 * PDF v1 uses plain typography; tables export as a bordered grid with wrapped cells (not WYSIWYG).
 * DOCX maps common marks and tables from the editor JSON.
 */
const RichTextEditor = ({
  value = '',
  inputFormat = 'auto',
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  disabled = false,
  readOnly = false,
  toolbarPreset = 'medium',
  toolbarActions = [],
  renderToolbarActions,
  extensions,
  aiActions = [],
  onAiAction,
  onAiPromptApply,
  aiPromptPlaceholder,
  downloadMenuEnabled = false,
  downloadTriggerLabel = 'Download',
  downloadTriggerAriaLabel,
  downloadPdfLabel,
  downloadDocxLabel,
  downloadFileNameBase = 'document',
  onExportStart,
  onExportSuccess,
  onExportError,
  className = '',
}) => {
  const isSyncingFromExternalRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const isLocked = disabled || readOnly;
  const [isAiPanelOpen, setIsAiPanelOpen] = useState(false);
  const hasAiFeatures = useMemo(
    () => aiActions.length > 0 || typeof onAiPromptApply === 'function',
    [aiActions, onAiPromptApply]
  );

  const extensionList = extensions ?? DEFAULT_EXTENSIONS;
  const normalizedValue = useMemo(
    () => normalizeEditorInput(value, inputFormat),
    [value, inputFormat]
  );
  const normalizedInputFormat = useMemo(
    () => (inputFormat === 'auto' ? detectInputFormat(value) : inputFormat),
    [inputFormat, value]
  );

  const editorExtensions = useMemo(
    () => [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'rte-table w-full border-collapse my-3 text-sm',
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          class:
            'rte-th border border-edge bg-surface-muted px-2 py-1.5 text-left font-semibold text-content',
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'rte-td border border-edge px-2 py-1.5 text-left align-top text-content',
        },
      }),
      ...extensionList,
    ],
    [placeholder, extensionList]
  );

  const editor = useEditor(
    {
      extensions: editorExtensions,
      content: normalizedValue,
      editable: !isLocked,
      // Toolbar subscribes via `useEditorState`; avoid re-rendering the whole editor on each keystroke.
      shouldRerenderOnTransaction: false,
      editorProps: {
        attributes: {
          class: EDITOR_PROSEMIRROR_CLASS,
        },
      },
      onUpdate: ({ editor: currentEditor }) => {
        if (isSyncingFromExternalRef.current) return;
        onChangeRef.current?.(getContentPayload(currentEditor), currentEditor);
      },
    }
  );

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isLocked);
  }, [editor, isLocked]);

  useEffect(() => {
    if (!editor) return;
    if (!isExternalValueDifferent(editor, normalizedValue)) return;

    isSyncingFromExternalRef.current = true;
    editor.commands.setContent(normalizedValue || '', false);
    queueMicrotask(() => {
      isSyncingFromExternalRef.current = false;
    });
  }, [editor, normalizedValue]);

  useEffect(() => {
    if (hasAiFeatures) return;
    setIsAiPanelOpen(false);
  }, [hasAiFeatures]);

  const runLinkAction = useCallback(() => {
    if (!editor || isLocked) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL', previousUrl);

    if (url === null) return;
    if (url.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor, isLocked]);

  const getEditorApi = useCallback(() => {
    if (!editor || isLocked) return null;

    return {
      replaceSelectionText: (text) => editor.chain().focus().insertContent(String(text ?? '')).run(),
      replaceSelectionHtml: (html) => editor.chain().focus().insertContent(String(html ?? '')).run(),
      insertTextAtCursor: (text) => editor.chain().focus().insertContent(String(text ?? '')).run(),
      replaceAllText: (text) => editor.commands.setContent(textToHtml(text), true),
      replaceAllHtml: (html) => editor.commands.setContent(String(html ?? ''), true),
      getInputFormat: () => normalizedInputFormat,
      getSelectionContext: () => getSelectionContext(editor),
      getText: () => editor.getText(),
      getHtml: () => editor.getHTML(),
    };
  }, [editor, isLocked, normalizedInputFormat]);

  const getSelectionContextForAi = useCallback(
    () => (editor ? getSelectionContext(editor) : null),
    [editor]
  );

  return (
    <div
      className={[
        'flex w-full min-w-0 flex-col overflow-hidden rounded-xl border border-edge-strong bg-surface',
        'shadow-sm dark:shadow-[0_4px_24px_rgba(0,0,0,0.35)]',
        isLocked ? 'opacity-80' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <RichTextEditorToolbar
        editor={editor}
        disabled={disabled}
        readOnly={readOnly}
        toolbarPreset={toolbarPreset}
        toolbarActions={toolbarActions}
        renderToolbarActions={renderToolbarActions}
        onRunLinkAction={runLinkAction}
        showAiToggle={hasAiFeatures}
        aiPanelOpen={isAiPanelOpen}
        onToggleAiPanel={() => setIsAiPanelOpen((v) => !v)}
        downloadMenuEnabled={downloadMenuEnabled}
        downloadTriggerLabel={downloadTriggerLabel}
        downloadTriggerAriaLabel={downloadTriggerAriaLabel}
        downloadPdfLabel={downloadPdfLabel}
        downloadDocxLabel={downloadDocxLabel}
        downloadFileNameBase={downloadFileNameBase}
        onExportStart={onExportStart}
        onExportSuccess={onExportSuccess}
        onExportError={onExportError}
      />

      {isAiPanelOpen ? (
        <RichTextEditorAiBar
          aiActions={aiActions}
          onAiAction={onAiAction}
          onAiPromptApply={onAiPromptApply}
          aiPromptPlaceholder={aiPromptPlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          getSelectionContext={getSelectionContextForAi}
          getEditorApi={getEditorApi}
        />
      ) : null}

      <RichTextEditorContent editor={editor} disabled={disabled} />
    </div>
  );
};

export default RichTextEditor;
